import { Autocomplete } from '@base-ui/react/autocomplete'
import {
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
  type RefCallback,
  useState,
} from 'react'
import { contentCopy } from '@/copy/content'
import { Alert } from '../Alert/Alert'
import { Button } from '../Button/Button'
import { cx } from '../cx'
import { Input } from '../Field/Field'
import { Kbd } from '../Kbd/Kbd'
import { Skeleton } from '../Loading/Loading'
import { useSkeletonDelay } from '../Loading/useSkeletonDelay'
import { Stack } from '../layout/Stack'
import { useOverlayLayer } from '../overlay/escapeStack'
import { overlayClass } from '../overlay/Overlay'
import { spaceOffset } from '../overlay/positioning'
import styles from './Combobox.module.css'

// Wraps Base UI Autocomplete (D95). Deviations: Escape keeps the text; a pick
// calls onSelect and never writes the input; the highlighted option has
// aria-selected; Escape via the page-wide stack. Tested.

export type ComboboxOption = {
  id: string
  label: string
  /** Adjacent options with the same group form one labelled group. */
  group?: string
  /** For example an Avatar; only when it varies within the list. */
  icon?: ReactNode
  /** A word at the end, text-mute. */
  meta?: string
  /** Kbd keys at the end. */
  shortcut?: readonly string[]
}

/** The field the wiring lands on: the default input, or a foreign textarea (D100). */
export type ComboboxFieldElement = HTMLInputElement | HTMLTextAreaElement

/** What `renderField` receives; it spreads onto an input or a textarea as is. */
export type ComboboxFieldProps = HTMLAttributes<ComboboxFieldElement> & {
  value?: string | number | readonly string[]
  placeholder?: string
  disabled?: boolean
  readOnly?: boolean
  autoComplete?: string
  ref?: RefCallback<ComboboxFieldElement>
}

type OptionGroup = { value: string; items: ComboboxOption[] }

function toGroups(options: readonly ComboboxOption[]): OptionGroup[] {
  const groups: OptionGroup[] = []
  for (const option of options) {
    const heading = option.group ?? ''
    const last = groups.at(-1)
    if (last && last.value === heading) last.items.push(option)
    else groups.push({ value: heading, items: [option] })
  }
  return groups
}

export function Combobox({
  label,
  value,
  onValue,
  options,
  onSelect,
  emptyMessage,
  openOnFocus = false,
  open,
  onOpenChange,
  autoHighlight = false,
  inline = false,
  pending,
  error,
  onRetry,
  placeholder,
  listLabel,
  renderField,
  ref,
}: {
  /** The input's accessible name. */
  label: string
  value: string
  onValue: (value: string) => void
  /** Already filtered and ranked. */
  options: readonly ComboboxOption[]
  onSelect: (option: ComboboxOption) => void
  emptyMessage: string
  /** Default false: typing opens it. */
  openOnFocus?: boolean
  /** Controlled open; otherwise internal. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Default false: never auto-select the first option. */
  autoHighlight?: boolean | 'always'
  /** The list renders in place, always open, with no popup. */
  inline?: boolean
  pending?: boolean
  error?: string
  onRetry?: () => void
  placeholder?: string
  /** The listbox's name; defaults to `label`. */
  listLabel?: string
  /** A foreign field; it must put the received props on the real input. */
  renderField?: (props: ComboboxFieldProps) => ReactElement
  ref?: Ref<ComboboxFieldElement>
}) {
  const [inner, setInner] = useState(false)
  const isOpen = open ?? inner
  const handle = useOverlayLayer(!inline && isOpen, (next: boolean) => {
    setInner(next)
    onOpenChange?.(next)
  })
  const showSkeleton = useSkeletonDelay(!!pending)
  const grouped = options.some((option) => option.group)

  function row(option: ComboboxOption) {
    return (
      <Autocomplete.Item
        key={option.id}
        value={option}
        onClick={() => onSelect(option)}
        className={styles.option}
        render={(props, state) => (
          // biome-ignore lint/a11y/useFocusableInteractive: focus stays on the input; the active option is aria-activedescendant
          <div {...props} role="option" aria-selected={state.highlighted} />
        )}
      >
        {option.icon && <span className={styles.icon}>{option.icon}</span>}
        <span className={styles.label}>{option.label}</span>
        {option.meta && <span className={styles.meta}>{option.meta}</span>}
        {option.shortcut && (
          <Kbd keys={option.shortcut} className={styles.end} />
        )}
      </Autocomplete.Item>
    )
  }

  // Base UI puts the combobox role and aria-expanded on a non-input field only
  // while the list is exposed; the wrapper keeps them on at all times.
  const expanded = inline || isOpen
  const field = (
    <Autocomplete.Input
      // Base UI types the field as an input; it forwards whatever element the
      // render prop draws, so a textarea ref is sound.
      ref={ref as Ref<HTMLInputElement>}
      aria-label={label}
      placeholder={placeholder}
      role="combobox"
      aria-expanded={expanded}
      aria-haspopup="listbox"
      aria-autocomplete="list"
      render={
        renderField ? (
          (props) => renderField(props as ComboboxFieldProps)
        ) : (
          <Input />
        )
      }
      onFocus={() => {
        if (!openOnFocus || inline) return
        setInner(true)
        onOpenChange?.(true)
      }}
    />
  )

  const results = (
    <div className={styles.results} data-inline={inline || undefined}>
      <Autocomplete.List
        className={styles.list}
        aria-label={listLabel ?? label}
        aria-busy={pending || undefined}
      >
        {grouped
          ? (group: OptionGroup) => (
              <Autocomplete.Group
                key={group.value}
                items={group.items}
                className={styles.group}
              >
                {group.value && (
                  <Autocomplete.GroupLabel className={styles.groupLabel}>
                    {group.value}
                  </Autocomplete.GroupLabel>
                )}
                <Autocomplete.Collection>
                  {(option: ComboboxOption) => row(option)}
                </Autocomplete.Collection>
              </Autocomplete.Group>
            )
          : (option: ComboboxOption) => row(option)}
      </Autocomplete.List>
      {showSkeleton && (
        <div aria-hidden="true">
          {[0, 1, 2].map((n) => (
            <div key={n} className={styles.skeletonRow}>
              <Skeleton width="60%" ground="surface" />
            </div>
          ))}
        </div>
      )}
      {!pending && !error && (
        <Autocomplete.Empty className={styles.empty}>
          {emptyMessage}
        </Autocomplete.Empty>
      )}
      {error && (
        <Alert tone="danger">
          <Stack gap="space-8" align="start">
            {error}
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                {contentCopy.combobox.retry}
              </Button>
            )}
          </Stack>
        </Alert>
      )}
    </div>
  )

  const content = inline ? (
    <>
      {field}
      {results}
    </>
  ) : (
    <>
      {field}
      <Autocomplete.Portal>
        <Autocomplete.Positioner
          side="bottom"
          align="start"
          sideOffset={spaceOffset('space-4')}
          className={styles.positioner}
        >
          <Autocomplete.Popup
            className={cx(overlayClass.surface, styles.popup)}
            data-weight="sm"
          >
            {results}
          </Autocomplete.Popup>
        </Autocomplete.Positioner>
      </Autocomplete.Portal>
    </>
  )

  const shared = {
    value,
    filter: null,
    itemToStringValue: (option: ComboboxOption) => option.label,
    autoHighlight,
    onValueChange: (
      next: string,
      details: Autocomplete.Root.ChangeEventDetails,
    ) => {
      // Base UI clears the text on Escape and writes the picked label into
      // the input; both are refused here.
      if (details.reason === 'escape-key' || details.reason === 'item-press') {
        return
      }
      onValue(next)
    },
    ...(inline
      ? { inline: true, open: true }
      : { open: isOpen, onOpenChange: handle }),
  }

  return grouped ? (
    <Autocomplete.Root {...shared} items={toGroups(options)}>
      {content}
    </Autocomplete.Root>
  ) : (
    <Autocomplete.Root {...shared} items={options}>
      {content}
    </Autocomplete.Root>
  )
}
